import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createRefetchContainer, graphql } from 'react-relay';
import serverVars from 'server-vars';
import { CUSTOMER_TYPE_TRADE } from '../../constants/userConstants';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';

/* Components */
import { SharedUserAndRelayVarLoader } from '../../shared/SharedUserAndRelayVarLoader';
import { SbSharedRefetchContainer } from '../SbSharedRefetchContainer/SbSharedRefetchContainer';
import { SbRespComponents } from '../SbRespComponents/SbRespComponents';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead';
import { SbSharedTrackingContainer } from '../SbSharedTrackingContainer/SbSharedTrackingContainer';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';

class SbRespLayoutComponent extends React.Component {
    constructor() {
        super();

        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.handleSbRefetch = this.handleSbRefetch.bind(this);

        this.relayVars = {};
    }

    handleUserRefetch(variables, ...args) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         *
         * Only do this after login, hence the server vars check, subsequent visits will depend on
         * the ut query param.
         */
        const fetchSb = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        this.doRefetch({ ...variables, fetchSb, skipUser: false }, ...args);
    }

    handleSbRefetch(variables, renderVariables, callback) {
        this.doRefetch({ ...variables, fetchSb: true }, renderVariables, () => {
            callback();

            this.props.runNotificationQueue();
        });
    }

    doRefetch(variables, renderVariables, callback) {
        this.relayVars = { ...this.relayVars, ...variables };
        this.props.relay.refetch(
            this.relayVars,
            { ...this.relayVars, ...renderVariables, fetchSb: true },
            callback
        );
    }

    render() {
        const { viewer, localeDomain, reinitTracking } = this.props;
        const user = viewer.user || null;
        const { searchBrowse } = viewer;

        return (
            <React.Fragment>
                <SharedUserAndRelayVarLoader onUserRefetch={this.handleUserRefetch} />
                <CurrencyProvider
                    render={({ currency }) => (
                        <React.Fragment>
                            <SbSharedTrackingContainer
                                searchBrowse={searchBrowse}
                                user={user}
                                currency={currency}
                                refineMenuFiltersMap={sbRespRefineMenuFiltersMap}
                                reinitTracking={reinitTracking}
                            />
                            <SbSharedHead searchBrowse={searchBrowse} localeDomain={localeDomain} />
                            <SbSharedRefetchContainer
                                searchBrowse={searchBrowse}
                                onSbRefetch={this.handleSbRefetch}
                            />
                            <SbRespComponents
                                viewer={viewer}
                                searchBrowse={searchBrowse}
                                user={user}
                                currency={currency}
                                isClient={this.relayVars.isClient}
                            />
                        </React.Fragment>
                    )}
                />
            </React.Fragment>
        );
    }
}

SbRespLayoutComponent.propTypes = {
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    localeDomain: PropTypes.string.isRequired,
    runNotificationQueue: PropTypes.func.isRequired,
    reinitTracking: PropTypes.func,
};

SbRespLayoutComponent.defaultProps = {
    runNotificationQueue: () => {},
};

const mapStateToProps = state => {
    const { intlVariables } = state;

    return {
        localeDomain: intlVariables.localeDomain,
    };
};

export const SbRespLayout = createRefetchContainer(
    connect(mapStateToProps)(SbRespLayoutComponent),
    {
        viewer: graphql`
            fragment SbRespLayout_viewer on Viewer
                @argumentDefinitions(fetchSb: { type: "Boolean", defaultValue: true }) {
                ...SbRespComponents_viewer
                searchBrowse(uriRef: $uriRef, first: $first, page: $page, dotComHost: $dotComHost)
                    @include(if: $fetchSb) {
                    ...SbSharedRefetchContainer_searchBrowse
                    ...SbRespComponents_searchBrowse
                    ...SbSharedHead_searchBrowse
                    ...SbSharedTrackingContainer_searchBrowse
                }
                user(userId: $userId) @include(if: $fetchUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespComponents_user
                }
            }
        `,
    },
    graphql`
        query SbRespLayoutRefetchQuery(
            $userId: String = ""
            $fetchUser: Boolean!
            $fetchSb: Boolean!
            $skipUser: Boolean = true
            $isTrade: Boolean!
            $uriRef: String
            $first: Int!
            $page: Int!
            $dotComHost: String = ""
            $showSeller: Boolean!
            $showModules: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH]
            $contentModuleId: String = ""
        ) {
            viewer {
                ...SbRespComponents_viewer @skip(if: $fetchSb)
                searchBrowse(uriRef: $uriRef, first: $first, page: $page, dotComHost: $dotComHost)
                    @include(if: $fetchSb) {
                    ...SbSharedRefetchContainer_searchBrowse
                    ...SbRespComponents_searchBrowse
                    ...SbSharedHead_searchBrowse
                    ...SbSharedTrackingContainer_searchBrowse
                }
                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespComponents_user
                }
            }
        }
    `
);
